import React, { useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { SIGN_OUT } from '../actions/types';
import history from '../history';
import { readAttemptSummary, clearAttemptSummary } from '../utils/attemptSummary';
import { startMyExam } from '../actions';

// Recovered from the previous Summary.js build cache.
export const Summary = ({ candidate: currentCandidate, match, dispatch }) => {
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState('');
    const id = match.params.id;
    const candidate = currentCandidate?.status === 'Submitted' && String(currentCandidate.id) === String(id)
        ? currentCandidate : readAttemptSummary(id);
    if (!candidate) return <Redirect to="/" />;

    const grade = candidate.grade?.data || candidate.grade || {};
    const scores = Array.isArray(grade.scores) ? grade.scores
        : Array.isArray(grade.subjects) ? grade.subjects : [];
    const total = grade.total ?? grade.totalScore;
    const seconds = Number(candidate.timeSpentInSeconds || 0);
    const attemptsUsed = Number(candidate.retakeCount || 0) + 1;
    const remaining = Math.max(0, 5 - attemptsUsed);
    const retake = async () => {
        setStarting(true);
        setError('');
        try {
            // Reauthenticate when returning from a cached summary or an expired session.
            await axios.post('/api/v1/auth/login', { registrationNumber: candidate.registrationNumber });
            await dispatch(startMyExam(candidate.id));
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.message || 'Unable to start another attempt. Please try again.');
        } finally {
            setStarting(false);
        }
    };
    const finish = () => {
        // The API now retains the session to allow retakes.
        axios.post('/api/v1/auth/candidate_logout').catch(() => {});
        clearAttemptSummary();
        ['currentSubject', 'request', 'timeSpent', 'timeLeft', 'shouldCount', 'totalAttempted'].forEach(key => localStorage.removeItem(key));
        dispatch({ type: SIGN_OUT });
        history.push('/');
    };
    const subjectName = (score, index) => {
        const code = score.subjectCode ?? score.integerCode ?? score.subject;
        const subject = candidate.subjects?.find(item => String(item.integerCode) === String(code) || item.name === score.subject);
        return subject?.name || score.subjectName || score.name || score.subject || candidate.subjects?.[index]?.name || `Subject ${index + 1}`;
    };

    return (
        <div className="container py-4">
            <div className="card text-center shadow">
                <div className="card-header"><h4 className="mb-0">Your Attempt Summary</h4></div>
                <div className="card-body">
                    <h5 className="card-title">Hi {candidate.name} ({candidate.registrationNumber})</h5>
                    <p>Your exam has been submitted successfully. See your scores below.</p>
                    <div className="table-responsive">
                        <table className="table table-success table-striped">
                            <thead><tr><th scope="col">Subject</th><th scope="col">Score</th></tr></thead>
                            <tbody>
                                {scores.length ? scores.map((score, index) => (
                                    <tr key={index}><td>{subjectName(score, index)}</td><td>{score.score ?? score.total ?? score.value ?? 'Unavailable'}</td></tr>
                                )) : <tr><td colSpan="2">Scores are currently unavailable. Please contact Admin.</td></tr>}
                            </tbody>
                            <tfoot><tr><th scope="row">Total</th><td>{total ?? 'Unavailable'}</td></tr></tfoot>
                        </table>
                    </div>
                    <p>Questions attempted: {candidate.totalAttempted ?? 0}</p>
                    <p>Time spent: {Math.floor(seconds / 60)} minutes {seconds % 60} seconds</p>
                    <p>Attempt {attemptsUsed} of 5 · {remaining} attempts remaining</p>
                    {remaining > 0 && !candidate.examEnded && <button disabled={starting} onClick={retake} className="btn btn-success mr-2">{starting ? 'Starting…' : 'Take another Exam'}</button>}
                    {remaining === 0 && <p>You have used all 5 attempts.</p>}
                    {error && <p role="alert" className="text-danger">{error}</p>}
                    <button onClick={finish} className="btn btn-danger">Logout</button>
                </div>
                <div className="card-footer text-muted">Wishing you all the best!</div>
            </div>
        </div>
    );
};

export default connect(state => ({ candidate: state.auth.user?.data }))(Summary);
